import React, { useMemo, useRef } from 'react';
import { Box, Text, Table, Thead, Tbody, Tr, Th, Td, Button, VStack } from '@chakra-ui/react';
import { Gantt, Task, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { ScheduleItem, FacilityItem, ReviewScheduleItem } from '../../contexts/types';
import { ReviewPhasePresets, CustomerSchedulePresets, FacilityItemPresets } from '../../contexts/constants';
import { useTranslation } from 'react-i18next';
import { parseLocalDateOnly, formatDateByLocale } from '../../utils/utils';
import "../common.css";

const ST = 'features.InvestmentScreening.ScheduleTable';

/**
 * この処理はガントチャートのロケールが英語(en)の場合、
 * ヘッダ部の月の表記をJanuary ⇒ Jan など略称表記にするためのものです。
 * ロケールは en のままだと他に影響を与える可能性があるため、'en-gantt'として
 * 定義するようにしています。
 */
if (typeof window !== 'undefined') {
  // ① Date.prototype.toLocaleDateString の上書き
  // (Date.prototype as any) で型チェックをバイパスし、引数をオプショナル（?）にしてシグネチャを合わせます
  const originalToLocaleDateString = Date.prototype.toLocaleDateString;
  (Date.prototype as any).toLocaleDateString = function (locales?: any, options?: any) {
    if (locales === 'en-gantt') {
      return originalToLocaleDateString.call(this, 'en', { ...options, month: 'short' });
    }
    return originalToLocaleDateString.call(this, locales, options);
  };

  // ② Intl.DateTimeFormat の上書き
  const OriginalDateTimeFormat = window.Intl.DateTimeFormat;

  // 新しいプロキシ関数を作成
  const PatchedDateTimeFormat = function (locales?: any, options?: any) {
    let targetLocales = locales;
    let targetOptions = options;

    if (locales === 'en-gantt' || (Array.isArray(locales) && locales.includes('en-gantt'))) {
      targetLocales = 'en';
      if (options && options.month === 'long') {
        targetOptions = { ...options, month: 'short' };
      }
    }
    // コンストラクタとして OriginalDateTimeFormat のインスタンスを生成して返す
    return new (OriginalDateTimeFormat as any)(targetLocales, targetOptions);
  };

  // 新しい関数の prototype にオリジナルの prototype を継承させる（エラー回避）
  PatchedDateTimeFormat.prototype = OriginalDateTimeFormat.prototype;

  if ('supportedLocalesOf' in OriginalDateTimeFormat) {
    (PatchedDateTimeFormat as any).supportedLocalesOf = OriginalDateTimeFormat.supportedLocalesOf;
  }

  // 読み取り専用エラーを回避するため、Object.defineProperty を使用して window.Intl.DateTimeFormat を上書き
  Object.defineProperty(window.Intl, 'DateTimeFormat', {
    value: PatchedDateTimeFormat,
    configurable: true,
    writable: true,
  });
}

/** 並べ替え操作をガント左列に渡すための状態・ハンドラ一式 */
export interface GanttReorderControls {
  isReorderMode: boolean;
  draggedIndex: number | null;
  isProcessing: boolean;
  toggleReorderMode: () => void;
  dragStart: (index: number) => void;
  dragOver: (e: React.DragEvent) => void;
  dragEnd: () => void;
  drop: (targetIndex: number) => void;
  moveUp: (index: number) => void;
  moveDown: (index: number) => void;
}

/** createGanttTaskListTable がクロージャで参照する可変状態（ref経由で常に最新値を保持） */
interface GanttTaskListTableState {
  scheduleReorder: GanttReorderControls;
  facilityReorder: GanttReorderControls;
  isReorderEnabled: boolean;
  reorderLabel: string;
  reorderCompleteLabel: string;
  scheduleItemCount: number;
  facilityItemCount: number;
}

/**
 * ガントチャート左列テーブル（タスク名列のみ表示、From/To列を非表示）を生成するクロージャ
 * gantt-task-react の TaskListTable は固定props（tasks/selectedTaskId等）しか受け取れないため、
 * 並べ替え関連の状態（isReorderMode等）はクロージャで包んでコンポーネントを生成する
 *
 * 引数を直接受け取らず ref を経由するのは、gantt-task-react が TaskListTable を
 * コンポーネントの type としてそのまま利用しているため。type（関数参照）が
 * レンダーごとに変わると React が別コンポーネントと判断し、ガント左列のDOMを
 * アンマウント→再マウントしてしまい、直後のドラッグ&ドロップ初回操作が失敗する。
 * ref経由にすることで関数参照を常に固定し、再マウントを防ぐ。
 */
const createGanttTaskListTable = (
  stateRef: React.MutableRefObject<GanttTaskListTableState>,
): React.FC<{
  rowHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  locale: string;
  tasks: Task[];
  selectedTaskId: string;
  setSelectedTask: (taskId: string) => void;
  onExpanderClick: (task: Task) => void;
}> => {
  return ({ rowHeight, rowWidth, fontFamily, fontSize, tasks, selectedTaskId, setSelectedTask, onExpanderClick }) => {
    const {
      scheduleReorder,
      facilityReorder,
      isReorderEnabled,
      reorderLabel,
      reorderCompleteLabel,
      scheduleItemCount,
      facilityItemCount,
    } = stateRef.current;
    // タスク行(milestone/task)のみを対象に、見出し行(project)を除いた並べ替え用インデックスを算出
    let scheduleRowIndex = -1;
    let facilityRowIndex = -1;

    return (
      <div style={{ fontFamily, fontSize }}>
        {tasks.map(t => {
          let expanderSymbol = '';
          if (t.hideChildren === false) expanderSymbol = '▼';
          else if (t.hideChildren === true) expanderSymbol = '▶';

          const isCustomerHeader = t.id === 'header-customer';
          const isFacilityHeader = t.id === 'header-facility';
          const isScheduleRow = t.id.startsWith('schedule-');
          const isFacilityRow = t.id.startsWith('facility-');

          if (isScheduleRow) scheduleRowIndex += 1;
          if (isFacilityRow) facilityRowIndex += 1;

          const reorder = isScheduleRow ? scheduleReorder : isFacilityRow ? facilityReorder : undefined;
          const rowIndex = isScheduleRow ? scheduleRowIndex : isFacilityRow ? facilityRowIndex : -1;
          const rowCount = isScheduleRow ? scheduleItemCount : isFacilityRow ? facilityItemCount : 0;

          return (
            <div
              key={t.id}
              draggable={isReorderEnabled && !!reorder?.isReorderMode}
              onDragStart={() => reorder?.dragStart(rowIndex)}
              onDragOver={reorder?.dragOver}
              onDrop={() => reorder?.drop(rowIndex)}
              onDragEnd={reorder?.dragEnd}
              style={{
                display: 'flex',
                height: rowHeight,
                alignItems: 'center',
                cursor: isReorderEnabled && reorder?.isReorderMode ? 'grab' : 'default',
                backgroundColor: reorder?.isReorderMode && reorder.draggedIndex === rowIndex
                  ? '#EBF8FF' // blue.50 相当
                  : selectedTaskId === t.id ? '#f0f0f0' : undefined,
                overflow: 'visible',
              }}
              onClick={() => setSelectedTask(t.id)}
            >
              <div style={{ minWidth: rowWidth, display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
                {expanderSymbol ? (
                  <span
                    style={{ marginRight: 4, cursor: 'pointer', fontSize: '0.7em' }}
                    onClick={e => { e.stopPropagation(); onExpanderClick(t); }}
                  >
                    {expanderSymbol}
                  </span>
                ) : (
                  <span style={{ marginRight: 4, width: '0.9em', display: 'inline-block' }} />
                )}

                {/* 並べ替えモード時のみ表示：▲▼ボタン（見出し行を除くタスク行のみ） */}
                {isReorderEnabled && reorder?.isReorderMode && rowIndex >= 0 && (
                  <VStack spacing={0} marginRight={2}>
                    <Button
                      type="button"
                      minWidth="14px"
                      width="14px"
                      height="14px"
                      variant="outline"
                      disabled={rowIndex === 0}
                      onClick={e => { e.stopPropagation(); reorder.moveUp(rowIndex); }}
                      style={{ fontSize: '0.8em', borderRadius: '3px', padding: '0px', cursor: rowIndex === 0 ? 'default' : 'pointer' }}
                    >
                      ▲
                    </Button>
                    <Button
                      type="button"
                      minWidth="14px"
                      width="14px"
                      height="14px"
                      variant="outline"
                      disabled={rowIndex === rowCount - 1}
                      onClick={e => { e.stopPropagation(); reorder.moveDown(rowIndex); }}
                      style={{ fontSize: '0.8em', borderRadius: '3px', padding: '0px', cursor: rowIndex === rowCount - 1 ? 'default' : 'pointer' }}
                    >
                      ▼
                    </Button>
                  </VStack>
                )}

                <span style={{ whiteSpace: 'nowrap' }}>{t.name}</span>

                {/* 見出し行にのみ表示：並び順変更ボタン（切替対象は各セクションのReorderControls） */}
                {isReorderEnabled && (isCustomerHeader || isFacilityHeader) && (
                  <Button
                    size="xs"
                    ml={2}
                    colorScheme={
                      (isCustomerHeader ? scheduleReorder.isReorderMode : facilityReorder.isReorderMode)
                        ? 'orange' : 'gray'
                    }
                    isDisabled={
                      (isCustomerHeader ? scheduleItemCount <= 1 || scheduleReorder.isProcessing
                        : facilityItemCount <= 1 || facilityReorder.isProcessing)
                    }
                    onClick={e => {
                      e.stopPropagation();
                      (isCustomerHeader ? scheduleReorder : facilityReorder).toggleReorderMode();
                    }}
                  >
                    {(isCustomerHeader ? scheduleReorder.isReorderMode : facilityReorder.isReorderMode)
                      ? reorderCompleteLabel : reorderLabel}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
}

interface ScheduleTableProps {
  scheduleItems: ScheduleItem[];
  facilityItems: FacilityItem[];
  reviewItems?: ReviewScheduleItem[];
  monthList: string[];
  grouped: { year: string; months: string[] }[];
  showTitle: boolean;
  /** ガントチャート左列の並べ替えUIを有効にするか（申請書画面では渡さない＝false扱い） */
  isReorderEnabled?: boolean;
  /** 顧客スケジュールの並べ替え操作（isReorderEnabled時のみ使用） */
  scheduleReorder?: GanttReorderControls;
  /** 設備投資スケジュールの並べ替え操作（isReorderEnabled時のみ使用） */
  facilityReorder?: GanttReorderControls;
}

const ScheduleTable: React.FC<ScheduleTableProps> = ({
  scheduleItems,
  facilityItems,
  reviewItems,
  showTitle = true,
  isReorderEnabled = false,
  scheduleReorder,
  facilityReorder,
  // monthList / grouped は PDF 側（generatePdf.ts ctx.project.monthList）で使用するため
  // 親から渡されるが、ガント表示では使用しない
}) => {
  const { t, i18n } = useTranslation();

  // 英語の時は独自ロケール 'en-gantt'、それ以外は そのまま使用
  const currentGanttLocale = i18n.language.startsWith('en') ? 'en-gantt' : i18n.language;

  /** 顧客スケジュールのプリセット値を翻訳済みラベルに変換 */
  const getScheduleLabel = (name: string) =>
    CustomerSchedulePresets.find(p => p.value === name)?.label ?? name;

  /** 設備投資スケジュールのプリセット値を翻訳済みラベルに変換 */
  const getFacilityLabel = (name: string) =>
    FacilityItemPresets.find(p => p.value === name)?.label ?? name;

  /**
   * gantt-task-react 用の Task 配列を構築する
   * 顧客スケジュール（project ヘッダ＋milestone）→ 設備投資スケジュール（project ヘッダ＋task）
   * 表示範囲が確定できない場合は空を返す
   */
  const tasks = useMemo((): Task[] => {
    const validSchedules = scheduleItems.filter(si => si.date);
    const validFacility = facilityItems.filter(fi => fi.startDate && fi.endDate);

    if (validSchedules.length === 0 && validFacility.length === 0) return [];

    // 全日付からガントの表示基準日を決める
    const allDates: Date[] = [
      ...validSchedules
        .map(si => parseLocalDateOnly(si.date))
        .filter((d): d is Date => d !== null),
      ...validFacility
        .flatMap(fi => [parseLocalDateOnly(fi.startDate), parseLocalDateOnly(fi.endDate)])
        .filter((d): d is Date => d !== null),
    ];
    if (allDates.length === 0) return [];
    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));

    const result: Task[] = [];

    // 顧客スケジュール ヘッダ行
    result.push({
      id: 'header-customer',
      name: t(`${ST}.customerSchedule`),
      start: minDate,
      end: minDate,
      type: 'project',
      progress: 0,
      hideChildren: false,
    });

    // 顧客スケジュール 各行（マイルストーン）
    validSchedules.forEach((item, idx) => {
      const d = parseLocalDateOnly(item.date);
      if (!d) return; // 不正フォーマットはスキップ（フィルタ済みの validSchedules だが二重の防御）
      result.push({
        id: `schedule-${item.id ?? idx}`,
        name: getScheduleLabel(item.name),
        start: d,
        end: d,
        type: 'milestone',
        progress: 0,
        project: 'header-customer',
      });
    });

    // 設備投資スケジュール ヘッダ行
    result.push({
      id: 'header-facility',
      name: t(`${ST}.investmentSchedule`),
      start: minDate,
      end: minDate,
      type: 'project',
      progress: 0,
      hideChildren: false,
    });

    // 設備投資スケジュール 各行（task バー）
    validFacility.forEach((fi, idx) => {
      const start = parseLocalDateOnly(fi.startDate);
      const end = parseLocalDateOnly(fi.endDate);
      if (!start || !end) return;
      // gantt-task-react は end > start が必要。同日の場合は milestone として扱う
      const isSameDay = start.getTime() === end.getTime();
      result.push({
        id: `facility-${fi.id ?? idx}`,
        name: getFacilityLabel(fi.name),
        start,
        end,
        type: isSameDay ? 'milestone' : 'task',
        progress: 0,
        project: 'header-facility',
        styles: {
          backgroundColor: '#90CDF4',        // blue.200 相当
          backgroundSelectedColor: '#63B3ED', // blue.300 相当
        },
      });
    });

    return result;
  }, [scheduleItems, facilityItems, t]);

  /** ガントチャート左列ヘッダ：タスク名列のみ表示（From/To列を非表示） */
  const GanttTaskListHeader: React.FC<{
    headerHeight: number;
    rowWidth: string;
    fontFamily: string;
    fontSize: string;
  }> = ({ headerHeight, rowWidth, fontFamily, fontSize }) => (
    <div style={{ fontFamily, fontSize }}>
      <div style={{ display: 'flex', listStyle: 'none', margin: 0, padding: 0, height: headerHeight - 2 }}>
        <div style={{ minWidth: rowWidth, display: 'flex', alignItems: 'center', paddingLeft: 8 }}>
          {/* 名称 */}
          {t(`${ST}.taskName`)}
        </div>
      </div>
    </div>
  );

  // 並べ替えUI付きの左列テーブル用の可変状態をrefで保持し、レンダーごとに最新値へ更新する
  // （関数コンポーネント自体の参照は固定し、gantt-task-react側での再マウントを防ぐ）
  const currentTaskListTableState: GanttTaskListTableState = {
    scheduleReorder: scheduleReorder ?? noopReorderControls,
    facilityReorder: facilityReorder ?? noopReorderControls,
    isReorderEnabled,
    reorderLabel: t(`${ST}.reorderChange`),
    reorderCompleteLabel: t(`${ST}.reorderDone`),
    scheduleItemCount: scheduleItems.filter(si => si.date).length,
    facilityItemCount: facilityItems.filter(fi => fi.startDate && fi.endDate).length,
  };
  const taskListTableStateRef = useRef<GanttTaskListTableState>(currentTaskListTableState);
  taskListTableStateRef.current = currentTaskListTableState;

  // 左列テーブルコンポーネントは初回のみ生成し、以降は同一の関数参照を使い続ける
  const TaskListTableWithReorder = useRef(
    createGanttTaskListTable(taskListTableStateRef)
  ).current;

  /** 日付を yyyy-mm-dd 形式に変換するヘルパー関数 */
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** ガントチャート用 カスタムツールチップ */
  const CustomTooltip: React.FC<{ task: Task; fontSize: string; fontFamily: string }> = ({
    task,
    fontFamily,
  }) => {
    // 必要に応じて t() を使って「開始」「終了」のラベルを多言語化できます
    const { t } = useTranslation();

    return (
      <Box
        bg="white"
        p={3}
        boxShadow="md"
        borderRadius="md"
        fontFamily={fontFamily}
        whiteSpace="nowrap"
        border="1px solid"
        borderColor="gray.200"
      >
        <Text fontWeight="bold" mb={2}>
          {task.name}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {t(`${ST}.from`)} {formatDate(task.start)}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {t(`${ST}.to`)} {formatDate(task.end)}
        </Text>
      </Box>
    );
  };

  return (
    <Box id="schedule-gantt-wrapper" boxShadow="md" p={4} mb={8} bg="white" overflowX="auto">
      {showTitle && (
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          {t(`${ST}.title`)}
        </Text>
      )}

      {isReorderEnabled && (
        (scheduleReorder?.isReorderMode || facilityReorder?.isReorderMode) && (
          <Box bg="orange.50" p={2} mb={4} borderRadius="md" borderWidth="1px" borderColor="orange.200">
            <Text color="orange.600" fontSize="sm">
              {t(`${ST}.reorderModeText`)}
            </Text>
          </Box>
        )
      )}

      {tasks.length > 0 ? (
        <Box id="schedule-gantt-container" overflowX="auto">
          <Gantt
            tasks={tasks}
            viewMode={ViewMode.Month}
            columnWidth={60}
            listCellWidth="220px"
            rowHeight={36}
            barCornerRadius={3}
            barFill={70}
            todayColor="rgba(255,165,0,0.3)"
            locale={currentGanttLocale}
            TaskListHeader={GanttTaskListHeader}
            TaskListTable={TaskListTableWithReorder}
            TooltipContent={CustomTooltip}
            // ドラッグ・リサイズはデータ変更を伴うため無効化
            onDateChange={undefined}
            onProgressChange={undefined}
          />
        </Box>
      ) : (
        <Text color="gray.400" fontSize="sm">
          {t(`${ST}.noData`)}
        </Text>
      )}

      {/* 振り返りスケジュール（2列テーブル形式を維持） */}
      {reviewItems && reviewItems.length > 0 && (
        <Box mt={6} data-html2canvas-ignore="true">
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            {t(`${ST}.reviewSchedule`)}
          </Text>
          <Table size="sm" variant="simple" style={{ tableLayout: 'fixed' }} maxW="400px" className='mm-ceas-table'>
            <Thead bg="gray.100">
              <Tr>
                <Th w="200px">{t(`${ST}.reviewPhase`)}</Th>
                <Th w="160px">{t(`${ST}.reviewDate`)}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {reviewItems.map((item, index) => {
                const phaseLabel = ReviewPhasePresets.find(p => p.value === item.phase)?.label ?? item.phase;
                return (
                  <Tr key={index}>
                    <Td>{phaseLabel}</Td>
                    <Td>{formatDateByLocale(item.date)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

/** isReorderEnabled=false 時に渡す no-op の並べ替え操作（未使用のため呼ばれない想定） */
const noopReorderControls: GanttReorderControls = {
  isReorderMode: false,
  draggedIndex: null,
  isProcessing: false,
  toggleReorderMode: () => { },
  dragStart: () => { },
  dragOver: () => { },
  dragEnd: () => { },
  drop: () => { },
  moveUp: () => { },
  moveDown: () => { },
};

export default ScheduleTable;
